<script setup lang="ts">
// Signed-in account dropdown in the topbar: identity, account links, sign
// out and a locale row (the utility band holding the main LanguageSwitcher
// folds away on scroll, so the locale stays reachable here). Closes on
// outside click, Escape and link click.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ChevronDown } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import LanguageSwitcher from './LanguageSwitcher.vue';

const { t } = useI18n();
const auth = useAuthStore();
const router = useRouter();

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

const userInitials = computed(() => {
  const first = auth.user?.firstName?.trim();
  const last = auth.user?.lastName?.trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  const username = auth.user?.username?.trim();
  if (username) return username.slice(0, 2).toUpperCase();
  return (auth.user?.email ?? '').slice(0, 2).toUpperCase() || 'U';
});

const displayName = computed(
  () =>
    [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(' ') ||
    auth.user?.username ||
    auth.user?.email?.split('@')[0] ||
    t('nav.yourAccount'),
);

const accountLinks = computed(() => [
  { to: '/account', label: t('nav.account') },
  { to: '/orders', label: t('nav.orders') },
  { to: '/account/billing', label: t('nav.billingWallet') },
  { to: '/account/settings', label: t('nav.settings') },
]);

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) queueMicrotask(() => document.addEventListener('click', onOutsideClick));
}
function closeMenu() {
  menuOpen.value = false;
  document.removeEventListener('click', onOutsideClick);
}
function onOutsideClick(e: MouseEvent) {
  if (menuRef.value && !menuRef.value.contains(e.target as Node)) closeMenu();
}

async function logout() {
  closeMenu();
  await auth.logout();
  router.push('/');
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeMenu();
}
onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  document.removeEventListener('click', onOutsideClick);
});
</script>

<template>
  <div ref="menuRef" class="relative">
    <button
      type="button"
      :aria-expanded="menuOpen"
      :aria-label="t('nav.accountMenu', { name: displayName })"
      class="flex h-10 items-center gap-2 rounded-lg border border-border bg-bg pl-1 pr-2 transition-colors hover:border-text-mute sm:pr-3"
      @click.stop="toggleMenu"
    >
      <span class="grid size-8 place-items-center rounded-md bg-brand-gradient font-display text-[12px] font-bold text-white">{{ userInitials }}</span>
      <span class="hidden max-w-[120px] truncate text-[13px] font-medium text-text sm:block">{{ displayName }}</span>
      <ChevronDown class="hidden size-3.5 text-text-mute sm:block" :stroke-width="2" />
    </button>

    <div
      v-if="menuOpen"
      role="menu"
      class="absolute right-0 top-[calc(100%+8px)] w-[240px] rounded-lg border border-border bg-surface p-1.5 shadow-lift"
    >
      <div class="px-3 pb-3 pt-2">
        <p class="truncate text-[14px] font-semibold text-text">{{ displayName }}</p>
        <p class="truncate text-[12px] text-text-dim">{{ auth.user?.email }}</p>
      </div>
      <div aria-hidden="true" class="mx-1 h-px bg-border-dim"></div>
      <ul class="py-1">
        <li v-for="link in accountLinks" :key="link.to">
          <RouterLink :to="link.to" role="menuitem" class="flex h-9 items-center rounded-md px-3 text-[14px] text-text hover:bg-card" @click="closeMenu">{{ link.label }}</RouterLink>
        </li>
      </ul>
      <div aria-hidden="true" class="mx-1 h-px bg-border-dim"></div>
      <button type="button" role="menuitem" class="mt-1 flex h-9 w-full items-center rounded-md px-3 text-[14px] text-sale hover:bg-card" @click="logout">{{ t('nav.signOut') }}</button>
      <!-- Always-reachable locale: the utility band folds away on scroll. -->
      <div aria-hidden="true" class="mx-1 mt-1 h-px bg-border-dim"></div>
      <div class="flex h-10 items-center justify-between pl-3 pr-1 pt-1">
        <span class="text-[13px] text-text-dim">{{ t('nav.language') }}</span>
        <LanguageSwitcher compact />
      </div>
    </div>
  </div>
</template>
